import { Injectable, inject, signal } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, tap, firstValueFrom, map } from 'rxjs';
import { environment } from '../../environments/environment';
import { APP_SCHEMA_ENDPOINTS, buildUrlWithParams, buildPaginationParams } from '../core/api';
import { ContactService } from '../core/services/contact.service';

interface UserInfo {
  user_id: number;
  email: string;
  contact_id?: number;
  language_id?: number;
  [key: string]: any;
}

interface UserSettingRead {
  user_setting_id: number;
  user_id: number;
  setting_id: number;
  value_text: string | null;
  value_int: number | null;
  value_fk: number | null;
}

interface SettingDefinitionRead {
  setting_id: number;
  setting_name: string;
  description?: string;
  value_type: 'text' | 'int' | 'fk';
  target_table?: string;
}

interface UserSettingWithDetails extends UserSettingRead {
  setting_name?: string;
  value_type?: string;
  value?: string;
}

/**
 * User Service
 *
 * Fetches user information from backend API.
 * Tests authentication and token validation.
 * Manages user settings (like language preference).
 */
@Injectable({ providedIn: 'root' })
export class UserService {
  private readonly http = inject(HttpClient);
  private readonly contactService = inject(ContactService);
  private readonly authServiceUrl = environment.api.authUrl;

  // Store user info
  private readonly userInfo = signal<UserInfo | null>(null);

  /**
   * Create HTTP headers with authorization token
   */
  private createHeaders(): HttpHeaders {
    const token = localStorage.getItem('access_token');
    return new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: token ? `Bearer ${token}` : '',
    });
  }

  /**
   * Get current user from backend (via token validation)
   *
   * This triggers the auth interceptor which adds the token.
   * Backend will validate the ext-auth token.
   */
  getCurrentUser(): Observable<any> {
    return this.http.get(`${this.authServiceUrl}/auth/me`).pipe(
      tap((user: any) => {
        this.userInfo.set(user);
      })
    );
  }

  /**
   * Get cached user info
   */
  getUserInfo(): UserInfo | null {
    return this.userInfo();
  }

  /**
   * Get current contact from ContactService
   * This is used to access the contact's language preference
   */
  getContactFromService() {
    return this.contactService.currentContact();
  }

  /**
   * Load contact by email address
   * Used by I18nStore to get contact's language preference on app start
   */
  async loadContactByEmail(email: string): Promise<any> {
    return firstValueFrom(this.contactService.getContactByEmail(email));
  }

  /**
   * Get all settings for a specific user
   * @param userId User ID to filter settings for
   */
  private async getUserSettings(userId: number): Promise<UserSettingWithDetails[]> {
    console.log(`📋 getUserSettings called for user_id: ${userId}`);
    const paginationParams = buildPaginationParams(0, 1000);
    const url = buildUrlWithParams(APP_SCHEMA_ENDPOINTS.userSettings.list, paginationParams);

    const allSettings = await firstValueFrom(
      this.http.get<UserSettingRead[]>(url, {
        headers: this.createHeaders(),
      })
    );

    console.log(`📦 Backend returned ${allSettings.length} total user_settings`);
    console.log('📦 Sample of all settings:', allSettings.slice(0, 3));

    // Filter by user_id
    const userSettings = allSettings.filter((s) => s.user_id === userId);
    console.log(`✅ Filtered to ${userSettings.length} settings for user_id ${userId}`);

    // Load setting definitions to get setting_name and value_type
    const definitions = await this.getSettingDefinitions();

    // Join user settings with definitions
    return userSettings.map((setting) => {
      const definition = definitions.find((def) => def.setting_id === setting.setting_id);

      // Extract unified value based on value_type
      let value: string = '';
      if (definition?.value_type === 'text' && setting.value_text) {
        value = setting.value_text;
      } else if (definition?.value_type === 'int' && setting.value_int !== null) {
        value = setting.value_int.toString();
      } else if (definition?.value_type === 'fk' && setting.value_fk !== null) {
        value = setting.value_fk.toString();
      }

      return {
        ...setting,
        setting_name: definition?.setting_name,
        value_type: definition?.value_type,
        value,
      };
    });
  }

  /**
   * Get all available setting definitions
   */
  private async getSettingDefinitions(): Promise<SettingDefinitionRead[]> {
    const paginationParams = buildPaginationParams(0, 1000);
    const url = buildUrlWithParams(APP_SCHEMA_ENDPOINTS.settings.list, paginationParams);

    return firstValueFrom(
      this.http.get<SettingDefinitionRead[]>(url, {
        headers: this.createHeaders(),
      })
    );
  }

  /**
   * Update contact's language preference in database
   *
   * students-lecturers-portal uses app.contacts, not app.users.
   * The contacts table has fk_language_id field, so we update that directly.
   */
  async updateUserLanguage(languageId: number): Promise<void> {
    try {
      // Get current contact from ContactService
      let contact = this.contactService.currentContact();

      // If contact not loaded yet, load it automatically
      if (!contact || !contact.contact_id) {
        console.log('📡 Contact not loaded yet, loading from backend...');

        // Get user email from auth token
        const userInfo = this.userInfo();
        let userEmail = userInfo?.email || userInfo?.['sub'];

        if (!userEmail) {
          console.log('📡 Loading user info to get email...');
          const user = await firstValueFrom(this.getCurrentUser());
          userEmail = user?.email || user?.['sub'];
        }

        if (!userEmail) {
          console.error('❌ No email found in user info');
          throw new Error('No email found in user profile');
        }

        console.log('🔍 Loading contact by email:', userEmail);
        contact = await firstValueFrom(this.contactService.getContactByEmail(userEmail));

        if (!contact || !contact.contact_id) {
          console.error('❌ Contact not found for email:', userEmail);
          throw new Error('Contact not found');
        }
      }

      // Update contact's language using ContactService
      const updateData = {
        fk_language_id: languageId,
      };

      await firstValueFrom(this.contactService.updateContact(contact.contact_id, updateData));

      // Update local user info
      this.userInfo.update((current) => {
        if (current) {
          return { ...current, language_id: languageId };
        }
        return current;
      });
    } catch (error) {
      console.error('❌ Failed to update contact language:', error);
      throw error;
    }
  }
}
